/**
 * **Qué bloques de una pantalla están abiertos y cuáles plegados**, recordado por usuario en el
 * navegador (#749).
 *
 * Dirección lo pidió con esas palabras el 20/10/2026 al probar el Centro de Cobros: *«el estado de
 * cada bloque se recuerda por usuario en el navegador»*. Vive aquí, en un util propio, porque lo
 * que decide cada pantalla es **qué bloques tiene y cuáles nacen abiertos**; cómo se guardan, no.
 * La segunda pantalla que lleve bloques plegables no tiene que volver a resolver el usuario, ni el
 * almacén bloqueado, ni la entrada corrupta.
 *
 * ## POR USUARIO, y eso no es un detalle
 *
 * Un puesto de oficina lo comparten varias personas y el navegador es el mismo. Las preferencias de
 * pantalla de quien entró antes no son nuestras para pisarlas, y tampoco se le pueden enseñar a
 * quien está delante ahora como si fueran suyas. La clave del almacén lleva dentro el usuario, así
 * que cada uno encuentra lo que dejó.
 *
 * **Sin usuario no se guarda nada.** Pasa cuando la pantalla se monta antes de que la sesión esté
 * cargada, y la alternativa —escribirlo en una entrada «de nadie»— es justo el reparto que esto
 * viene a evitar: lo primero que haría esa entrada es acabar en la pantalla de otra persona. El
 * estado sigue funcionando en memoria, lo que se pierde es solo el recuerdo al recargar.
 *
 * ## LO QUE SE GUARDA ES LO QUE LA PANTALLA DECLARA, Y NADA MÁS
 *
 * `inicial` solo devuelve las claves de `porOmision`. Un bloque que se quitó de la pantalla deja su
 * apunte en el navegador —no hay nadie que lo limpie— y leerlo a ciegas devolvería estado de un
 * bloque que ya no existe; y un valor que no sea booleano (un apunte de una versión anterior que
 * guardaba otra cosa) se descarta y vale la omisión. Lo que la pantalla no declara no se recuerda.
 *
 * ## APUNTA UNA CLAVE, NO EL ESTADO ENTERO
 *
 * `apuntar` lee lo guardado, cambia **esa** clave y lo vuelve a escribir. Hace falta porque una
 * misma pantalla puede tener bloques en componentes distintos —en el Centro de Cobros, «Comportamiento
 * por forma de pago» es un componente propio— y los dos escriben en la misma entrada: con el estado
 * entero, el último en guardar borraría los bloques que no conoce.
 *
 * @param {string} pantalla  cómo se llama la pantalla en el almacén; no se traduce ni se enseña
 * @param {string} usuario   el `username` de quien está delante, o nada para no guardar
 * @param {Object} porOmision clave de bloque → si nace abierto
 */
const PREFIJO = 'simecal-plegado:';

export default function plegadoDeBloques(pantalla, usuario, porOmision) {
  const omisiones = porOmision || {};
  const claveAlmacen = usuario ? PREFIJO + pantalla + ':' + usuario : null;

  /** Lo apuntado para este usuario en esta pantalla, o nada si no hay o no se puede leer. */
  function apuntado() {
    if (!claveAlmacen) return {};
    try {
      const crudo = window.localStorage.getItem(claveAlmacen);
      const leido = crudo ? JSON.parse(crudo) : null;
      // Un valor que no es un objeto es un apunte corrupto: se descarta entero y manda la omisión.
      // Leerlo igual dejaría `estado[bloque]` con trozos de una cadena o de un array.
      return leido && typeof leido === 'object' && !Array.isArray(leido) ? leido : {};
    } catch (noSePudoLeer) {
      // Leer el almacén lanza con el almacenamiento desactivado (modo privado de Safari). Sin
      // apuntes legibles, la pantalla nace con sus omisiones, que es lo mismo que la primera vez.
      return {};
    }
  }

  return {
    /** El estado con el que nace la pantalla: lo apuntado por encima de las omisiones. */
    inicial: function() {
      const guardado = apuntado();
      const estado = {};
      Object.keys(omisiones).forEach(function(bloque) {
        const valor = guardado[bloque];
        estado[bloque] = typeof valor === 'boolean' ? valor : !!omisiones[bloque];
      });
      return estado;
    },

    /**
     * Apunta que un bloque queda abierto o cerrado. Devuelve si se ha podido guardar.
     *
     * Quien llama **no deja de plegar el bloque** si esto dice que no: lo que se pierde es el
     * recuerdo, no la acción que acaba de hacer la persona. Es al contrario que los apuntes de las
     * operaciones de escritura (`operacionesAbonosCobros`), donde un apunte que no se guarda sí
     * obliga a no mandar la petición porque su clave es lo único con lo que se puede preguntar
     * después qué pasó. Aquí no hay nada que preguntar: es una preferencia de pantalla.
     */
    apuntar: function(bloque, abierto) {
      if (!claveAlmacen) return false;
      const guardado = apuntado();
      guardado[bloque] = !!abierto;
      try {
        window.localStorage.setItem(claveAlmacen, JSON.stringify(guardado));
        return true;
      } catch (noSePudoEscribir) {
        console.warn('No se ha podido recordar el estado de los bloques de ' + pantalla,
          noSePudoEscribir);
        return false;
      }
    }
  };
}
